/**
 * `useRouteMeta` — what the current URL is called, in the active language.
 *
 * `state`:
 *   - `registered`: the route is in `ROUTE_REGISTRY`; `title` is its heading text.
 *   - `pending`:    the route exists but is in `PENDING_ROUTES`; nobody touches the title.
 *   - `unknown`:    no route of the registry or the pending list; `title` is the
 *                   «page not found» text.
 *
 * `title` is `null` while its namespace is still loading (never the raw key),
 * so the tab never flashes `transactions:accessibility.pageHeading`.
 */

import type { Namespace } from '@platform/i18n/config'
import { useTranslation } from '@platform/i18n/hooks/useTranslation'
import { useLocation } from 'react-router-dom'

import { getRouteMeta, isPendingRoute } from './route-registry'

type RouteTitleState = 'registered' | 'pending' | 'unknown'

interface RouteTitle {
  state: RouteTitleState
  title: string | null
}

const NOT_FOUND_KEY = 'common:routes.notFound'

const namespaceOf = (titleKey: string): Namespace => titleKey.split(':')[0] as Namespace

export function useRouteMeta(): RouteTitle {
  const { pathname } = useLocation()
  const meta = getRouteMeta(pathname)
  const state: RouteTitleState = meta
    ? 'registered'
    : isPendingRoute(pathname)
      ? 'pending'
      : 'unknown'
  const titleKey = meta ? meta.titleKey : NOT_FOUND_KEY
  const { t, ready } = useTranslation([namespaceOf(titleKey), 'common'])

  if (state === 'pending') return { state, title: null }
  const text = t(titleKey)
  return { state, title: ready && text !== '' && text !== titleKey ? text : null }
}
